import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ErrorBoundary } from './components/ErrorBoundary';
import { RetryableErrorBoundary } from './components/RetryableErrorBoundary';
import { Navigation } from './components/Navigation';
import { FallbackComponent } from './components/FallbackComponent';
import { RealTimeNotifications } from './components/RealTimeNotifications/RealTimeNotifications';
import { initializeAnalytics, trackPageView } from './utils/analytics/googleAnalytics';
import { registerSW } from './utils/serviceWorker';
import { createTracer } from './utils/telemetry/tracer';
import performanceMonitor from './utils/monitoring/performanceMonitor';
import userDataService from './services/UserDataService';
import './App.css';

// Lazy load pages for performance
const Home = lazy(() => import('./pages/Home'));
const Dashboard = lazy(() => import('./components/Dashboard/Dashboard'));
const DynamicQuizPage = lazy(() => import('./pages/DynamicQuizPage'));
const Profile = lazy(() => import('./pages/Profile'));

// Category pages
const CareerHome = lazy(() => import('./pages/career/Home'));
const CareerQuiz = lazy(() => import('./pages/career/Quiz'));
const CareerResults = lazy(() => import('./pages/career/Results'));
const LifestyleHome = lazy(() => import('./pages/lifestyle/Home'));
const LifestyleQuiz = lazy(() => import('./pages/lifestyle/Quiz'));
const LifestyleResults = lazy(() => import('./pages/lifestyle/Results'));
const PsychologyHome = lazy(() => import('./pages/psychology/Home'));
const PsychologyQuiz = lazy(() => import('./pages/psychology/Quiz'));
const PsychologyResults = lazy(() => import('./pages/psychology/Results'));
const RelationshipsHome = lazy(() => import('./pages/relationships/Home'));
const RelationshipsQuiz = lazy(() => import('./pages/relationships/Quiz'));
const RelationshipsResults = lazy(() => import('./pages/relationships/Results'));

// PWA features
let deferredPrompt = null;

function App() {
  const [theme, setTheme] = useState(() => {
    // Check saved preference
    const saved = localStorage.getItem('theme');
    if (saved) return saved;
    
    // Check system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  });
  
  const [user, setUser] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [installPromptVisible, setInstallPromptVisible] = useState(false);
  const [globalError, setGlobalError] = useState(null);
  const [activityLevel, setActivityLevel] = useState(() => {
    // Randomly assign activity level for session
    const random = Math.random();
    if (random > 0.85) return 'viral'; // 15% chance
    if (random > 0.55) return 'busy';  // 30% chance
    if (random > 0.25) return 'normal'; // 30% chance
    return 'quiet'; // 25% chance
  });

  // Initialize app
  useEffect(() => {
    // Initialize user data service
    userDataService.initialize();
    
    // Initialize analytics (only if GA ID is available)
    const gaId = import.meta.env.VITE_GA_MEASUREMENT_ID;
    if (gaId) {
      initializeAnalytics(gaId);
      // Track initial page view only after analytics is initialized
      trackPageView(window.location.pathname);
    }
    
    // Initialize telemetry
    try {
      createTracer('quiz-app', '1.0.0');
    } catch (error) {
      console.warn('Telemetry initialization failed:', error);
    }
    
    // Register service worker
    registerSW();
    
    // Start performance monitoring
    if (performanceMonitor && performanceMonitor.mark) {
      performanceMonitor.mark('app-init');
    }
  }, []);

  // Theme management
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  // PWA install prompt
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      deferredPrompt = e;
      setInstallPromptVisible(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('App installed');
      trackPageView('/app-installed');
    }
    
    deferredPrompt = null;
    setInstallPromptVisible(false);
  };

  // Network status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Global error handling
  useEffect(() => {
    const handleError = (event) => {
      console.error('Global error:', event.error);
      setGlobalError(event.error);
      trackPageView('/error');
    };

    const handleUnhandledRejection = (event) => {
      console.error('Unhandled rejection:', event.reason);
      setGlobalError(event.reason);
      trackPageView('/error');
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('user');
  };

  const handleErrorRecovery = () => {
    setGlobalError(null);
    window.location.reload();
  };

  // Prefetch critical routes
  useEffect(() => {
    const prefetchRoutes = async () => {
      // Dynamically import to prefetch
      import('./pages/Home');
      import('./components/Dashboard/Dashboard');
    };
    
    // Prefetch after initial load
    setTimeout(prefetchRoutes, 2000);
  }, []);

  if (globalError) {
    return (
      <div className="app" data-testid="app">
        <div className="global-error">
          <h1>Something went wrong</h1>
          <p>{globalError.message || 'An unexpected error occurred'}</p>
          <button onClick={handleErrorRecovery}>Reload App</button>
        </div>
      </div>
    );
  }

  return (
    <HelmetProvider>
      <ErrorBoundary>
        <RetryableErrorBoundary>
          <Router>
            <div className="app" data-testid="app" data-theme={theme}>
            <Navigation 
              user={user}
              onLogout={handleLogout}
              showStreak={true}
              streakCount={7}
              onToggleTheme={toggleTheme}
              currentTheme={theme}
            />
            
            <main 
              className="app-main"
              role="main"
              aria-label="Main content"
            >

              {!isOnline && (
                <div className="offline-banner" role="status" aria-live="polite">
                  You are currently offline. Some features may be limited.
                </div>
              )}

              <Suspense fallback={<FallbackComponent />}>
                <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/dashboard" element={<Dashboard user={user} />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/quiz/:quizId" element={<DynamicQuizPage />} />
                
                {/* Category routes */}
                <Route path="/career" element={<CareerHome />} />
                <Route path="/career/quiz/:slug" element={<CareerQuiz />} />
                <Route path="/career/results/:slug" element={<CareerResults />} />
                
                <Route path="/lifestyle" element={<LifestyleHome />} />
                <Route path="/lifestyle/quiz/:slug" element={<LifestyleQuiz />} />
                <Route path="/lifestyle/results/:slug" element={<LifestyleResults />} />
                
                <Route path="/psychology" element={<PsychologyHome />} />
                <Route path="/psychology/quiz/:slug" element={<PsychologyQuiz />} />
                <Route path="/psychology/results/:slug" element={<PsychologyResults />} />
                
                <Route path="/relationships" element={<RelationshipsHome />} />
                <Route path="/relationships/quiz/:slug" element={<RelationshipsQuiz />} />
                <Route path="/relationships/results/:slug" element={<RelationshipsResults />} />
                
                {/* 404 - Use Home for now */}
                <Route path="/404" element={<Home />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </main>

          <div 
            className="theme-toggle" 
            onClick={toggleTheme}
            role="button"
            aria-label="Toggle theme"
            tabIndex={0}
            onKeyPress={(e) => e.key === 'Enter' && toggleTheme()}
          >
            {theme === 'light' ? '🌙' : '☀️'}
          </div>
          
          {/* Real-time activity notifications for social proof */}
          <RealTimeNotifications 
            enabled={true}
            position="bottom-left"
            maxNotifications={3}
            displayDuration={6000}
            interval={activityLevel === 'viral' ? 4000 : 
                     activityLevel === 'busy' ? 6000 :
                     activityLevel === 'normal' ? 8000 : 12000}
            activityLevel={activityLevel}
          />
        </div>
        </Router>
      </RetryableErrorBoundary>
    </ErrorBoundary>
    </HelmetProvider>
  );
}

// 404 Component
function NotFound() {
  return (
    <div className="not-found">
      <h1>404</h1>
      <p>Page not found</p>
      <a href="/">Go home</a>
    </div>
  );
}

export { App };
export default App;// Rebuild trigger: Thu Sep 11 10:23:41 PM UTC 2025
